import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
const PRODUCT_IMAGE = 'https://uploadthingy.s3.us-west-1.amazonaws.com/f9dftiuyCXSegMt95tQFws/Mockup_%28ROMPE_LA_MATRIX%29.png';
const NAMES = ['Andrea M.', 'Ricardo X.', 'Lucía R.', 'Julián P.', 'María G.', 'Sebastián L.', 'Emily J.', 'Sophia K.', 'Liam D.', 'Chloe B.', 'Enzo R.', 'Valentina C.', 'Oliver T.'];
const toastCopy = {
  es: {
    label: 'COMPRA RECIENTE',
    line3: (price: string) => `Edición digital • $${price} USD`
  },
  en: {
    label: 'RECENT PURCHASE',
    line3: (price: string) => `Digital edition • $${price} USD`
  }
};
const getRandomName = () => NAMES[Math.floor(Math.random() * NAMES.length)];
const getRandomPrice = () => (Math.random() * (23.97 - 7.97) + 7.97).toFixed(2);
const SESSION_STORAGE_KEY = 'recentPurchaseToastDismissed';
const MAX_SHOWS_PER_SESSION = 5;
const SHOW_DURATION = 6000; // 6 seconds visible
const MIN_INTERVAL = 9000; // 9 seconds
const MAX_INTERVAL = 18000; // 18 seconds
export function RecentPurchaseToast() {
  const {
    language,
    hasSelectedLanguage
  } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showCount, setShowCount] = useState(0);
  const [currentName, setCurrentName] = useState(getRandomName());
  const [currentPrice, setCurrentPrice] = useState(getRandomPrice());
  // Check if user dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);
  useEffect(() => {
    // NO mostrar popup si el usuario NO ha seleccionado idioma
    if (!hasSelectedLanguage) return;
    if (isDismissed || showCount >= MAX_SHOWS_PER_SESSION) return;
    const scheduleNextPopup = (isFirst: boolean = false) => {
      // Calculate delay: first popup 5-8s, subsequent popups 9-18s
      const delay = isFirst ? Math.random() * 3000 + 5000 // 5-8 seconds for first
      : Math.random() * (MAX_INTERVAL - MIN_INTERVAL) + MIN_INTERVAL; // 9-18 seconds
      const showTimer = setTimeout(() => {
        // Generate new random data
        setCurrentName(getRandomName());
        setCurrentPrice(getRandomPrice());
        // Show popup
        setIsVisible(true);
        setShowCount(prev => prev + 1);
        // Hide after 6 seconds, then schedule next popup
        const hideTimer = setTimeout(() => {
          setIsVisible(false);
          // Schedule next popup AFTER this one hides (if not at max)
          if (showCount + 1 < MAX_SHOWS_PER_SESSION && !isDismissed) {
            scheduleNextPopup(false);
          }
        }, SHOW_DURATION);
        return () => clearTimeout(hideTimer);
      }, delay);
      return () => clearTimeout(showTimer);
    };
    // Start the cycle with first popup
    const cleanup = scheduleNextPopup(true);
    return cleanup;
  }, [isDismissed, hasSelectedLanguage]); // Agregar hasSelectedLanguage como dependencia
  const handleClose = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  };
  const {
    label,
    line3
  } = toastCopy[language];
  return <AnimatePresence data-id="element-210">
      {isVisible && !isDismissed && <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 50,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed z-50" style={{
      bottom: '1.5rem',
      left: '1.5rem',
      maxWidth: '300px'
    }} data-id="element-211">
          {/* Compact toast card */}
          <div className="relative bg-[#0a0e1a]/95 backdrop-blur-md border border-[#00d2f7]/50 rounded-full shadow-2xl overflow-hidden" data-id="element-212">
            {/* Subtle glow effect */}
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{
          boxShadow: '0 0 20px rgba(0, 210, 247, 0.2)'
        }} data-id="element-213" />

            <div className="relative z-10 px-3 py-2.5 flex items-center gap-2.5" data-id="element-214">
              {/* Product Image - Compact */}
              <div className="flex-shrink-0" data-id="element-215">
                <img src={PRODUCT_IMAGE} alt="Product" className="w-12 h-12 rounded-lg object-cover" style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }} data-id="element-216" />
              </div>

              {/* Content - Compact 3 lines */}
              <div className="flex-1 min-w-0 pr-6" data-id="element-217">
                {/* Line 1: Label with green dot */}
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-218">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" style={{
                boxShadow: '0 0 4px rgba(74, 222, 128, 0.8)'
              }} data-id="element-219" />
                  <span className="text-[#00d2f7] text-[9px] font-bold uppercase tracking-wider" data-id="element-220">
                    {label}
                  </span>
                </div>

                {/* Line 2: Buyer name */}
                <p className="text-white font-bold text-sm leading-tight mb-0.5 truncate" data-id="element-221">
                  {currentName}
                </p>

                {/* Line 3: Product info */}
                <p className="text-white/60 text-xs leading-tight" data-id="element-222">
                  {line3(currentPrice)}
                </p>
              </div>

              {/* Close button - Positioned absolute top-right */}
              <button onClick={handleClose} className="absolute top-2 right-2 text-white/40 hover:text-white transition-colors" aria-label="Close" style={{
            padding: '2px'
          }} data-id="element-223">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-224">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-225" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}